import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserPlus } from "lucide-react";
import { api, API, fmtDDMMYYYY } from "../../api";
import { PageHeader, Panel, Btn, Input, TextArea, Empty, Pill } from "../../ui";

export default function CustomersPage() {
  const [list, setList] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", employment_status: "employed", employer: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`${API}/customers`).then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`${API}/customers`, {
        primary_applicant: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        joint_applicants: [],
        address_history: form.address ? [{ from: "present", address: form.address }] : [],
        employment: { status: form.employment_status, employer: form.employer },
        dependants: [],
        notes: form.notes,
      });
      toast.success("Customer created");
      setShowNew(false);
      setForm({ name: "", email: "", phone: "", address: "", employment_status: "employed", employer: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Primary applicants, joint applicants, address history, employment and dependants. Access to each applicant's information is scoped by what they're authorised to see." action={<Btn onClick={() => setShowNew(true)} data-testid="customers-new"><UserPlus size={14} /> New customer</Btn>} />
      {showNew && (
        <Panel className="p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">New customer</h3>
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Full name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} testid="customer-name" />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} testid="customer-email" />
            <Input label="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} testid="customer-phone" />
            <Input label="Current address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} testid="customer-address" />
            <Input label="Employer" value={form.employer} onChange={(e) => setForm((f) => ({ ...f, employer: e.target.value }))} testid="customer-employer" />
            <div className="sm:col-span-2">
              <TextArea label="Notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} testid="customer-notes" />
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end pt-2">
              <Btn variant="ghost" type="button" onClick={() => setShowNew(false)}>Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="customer-save">Save</Btn>
            </div>
          </form>
        </Panel>
      )}
      {list === null && <p className="text-sm text-slate-500">Loading…</p>}
      {list && list.length === 0 && <Empty title="No customers yet" hint="Create your first customer to get started." testid="customers-empty" />}
      {list && list.length > 0 && (
        <Panel>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10.5px] tracking-[0.14em] uppercase text-slate-500 bg-slate-50">
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Joint</th>
                <th className="text-left px-5 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c, i) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${i+1}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{c.primary_applicant?.name}</td>
                  <td className="px-5 py-3 text-slate-600">{c.primary_applicant?.email || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.primary_applicant?.phone || "—"}</td>
                  <td className="px-5 py-3">{c.joint_applicants?.length > 0 ? <Pill tone="sky">+{c.joint_applicants.length}</Pill> : "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtDDMMYYYY(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
